import React, { useRef, useState, useEffect } from "react";
import { getFirestore, doc, getDoc, onSnapshot, collection, query, where, limit, getDocs } from "firebase/firestore";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import app from "../../firebase";
import { clearEnrolledSession } from "../../auth/roleSession";
import { getActiveSemester, getAnnouncementSchoolYear, useCalendarClock } from "../../utils/announcementDates";

function EnrolledCORContent({ student, miscFees = [], cashDiscount = 0, cashWithDiscountDate = "", cashWithoutDiscountDate = "", installmentDates = [], selectedSchoolYear = "", selectedTerm = "" }) {
  const corRef = useRef();

  if (!student) {
    return (
      <div style={{ padding: 28, color: "#444", textAlign: "center" }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>No student data</div>
        <div style={{ color: "#666" }}>COR is not available — student object is missing.</div>
      </div>
    );
  }
  function formatDate(dateStr) {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  }

  const studentName = `${student.lastName}, ${student.firstName}`;
  const studentIdNum = student.studentId || student.idNumber || "N/A";
  const program = student.program || "N/A";
  const semester = selectedTerm || student.cor?.corTerm || student.preRegistration?.semester || student.semester || "First Semester";
  const schoolYear = selectedSchoolYear || student.cor?.corAY || student.AY || "2025-2026";

  // Get all schedules from termSchedules object
  const allSchedules = [];
  if (student.termSchedules && typeof student.termSchedules === 'object') {
    Object.entries(student.termSchedules).forEach(([termKey, schedArray]) => {
      if (Array.isArray(schedArray)) {
        allSchedules.push(...schedArray);
      }
    });
  }

  // Filter schedules based on selected school year and semester
  const filteredSchedules = allSchedules.filter(schedule => {
    const scheduleYear = schedule.schoolYear || "";
    const scheduleSemester = schedule.semester || "";
    return scheduleYear === selectedSchoolYear && scheduleSemester === selectedTerm;
  });

  // Get year level from filtered schedules
  const yearLevel = filteredSchedules.length > 0 
    ? (filteredSchedules[0].yearLevel || "N/A")
    : (student.cor?.corEntryLevel || student.preRegistration?.entryLevel || "N/A");

  // Group courses
  const groupedCourses = filteredSchedules.length > 0
    ? Object.values(
        filteredSchedules.reduce((acc, course) => {
          const key = [course.courseId, course.courseName, course.section].join('|');
          if (!acc[key]) {
            acc[key] = {
              courseId: course.courseId,
              courseName: course.courseName,
              section: course.section,
              units: course.units,
              meetings: []
            };
          }
          acc[key].meetings.push({
            day: course.day,
            time: course.time,
            room: course.room
          });
          return acc;
        }, {})
      )
    : [];

  const totalUnits = groupedCourses.length > 0
    ? groupedCourses.reduce((sum, c) => sum + (parseFloat(c.units) || 0), 0)
    : 0;

  // Calculate fees
  let total = 0;
  const fees = miscFees.map((fee) => {
    let amount = 0;
    let formula = "";
    if (fee.formula?.toLowerCase().includes("units") && typeof fee.amount === "number") {
      amount = totalUnits * fee.amount;
      formula = `(${totalUnits} units X ${fee.amount})`;
    } else if (fee.perUnit !== undefined) {
      amount = totalUnits * fee.perUnit;
      formula = `(${totalUnits} units X ${fee.perUnit})`;
    } else if (fee.formula) {
      formula = fee.formula;
      amount = fee.amount || 0;
    } else {
      amount = fee.amount || 0;
    }
    total += amount;
    return { ...fee, amount, formula };
  });

  const totalWithDiscount = total - cashDiscount;
  const installment = parseFloat((total / 5).toFixed(2));

  function handlePrint() {
    const printWindow = window.open("", "_blank", "width=900,height=1200");
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
      <head>
        <title>Certificate of Registration</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
          .container { width: 900px; margin: 0 auto; padding: 32px; }
          .header { text-align: center; margin-bottom: 18px; }
          .subtitle { font-size: 20px; margin-bottom: 18px; }
          .info-row { display: flex; justify-content: space-between; margin-bottom: 12px; }
          .subject-table { width: 100%; border-collapse: collapse; margin-bottom: 18px; }
          .subject-table th, .subject-table td { padding: 6px 8px; font-size: 15px; border-bottom: 1px solid #bbb; }
          .subject-table th { background: #f7f7f7; }
          .subject-table td.day-cell > div, .subject-table td.schedule-cell > div { white-space: pre-line; }
          .fees-table th, .fees-table td { border: none; padding: 6px 8px; font-size: 15px; }
          .fees-table th { background: none; }
          .fees-title { font-weight: bold; font-size: 17px; margin-bottom: 8px; }
          .option-title { font-weight: bold; color: #18803b; font-size: 16px; margin-top: 18px; }
          .note { background: #fff8f1; border: 1px solid #ff9800; border-radius: 6px; padding: 12px; color: #e67e22; font-size: 14px; margin-top: 18px; }
          .print-footer { font-size: 13px; margin-top: 24px; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <div style="font-size:22px;font-weight:bold;">Manila Adventist College</div>
            <div style="font-size:15px;">CERTIFICATE OF REGISTRATION</div>
            <div class="subtitle">${schoolYear} ${semester}</div>
          </div>
          <div class="info-row">
            <div>Name: <b>${studentName}</b></div>
            <div>Student ID: <b>${studentIdNum}</b></div>
          </div>
          <div class="info-row">
            <div>Program: <b>${program}</b></div>
            <div>Year: <b>${yearLevel}</b></div>
          </div>
          <table class="subject-table">
            <thead>
              <tr>
                <th>Subject Code</th>
                <th>Subject Description</th>
                <th>Section</th>
                <th>Day</th>
                <th>Schedule</th>
                <th>Room</th>
                <th>Units</th>
              </tr>
            </thead>
            <tbody>
              ${groupedCourses.map(row => `
                <tr>
                  <td>${row.courseId || ""}</td>
                  <td>${row.courseName || ""}</td>
                  <td>${row.section || ""}</td>
                  <td class="day-cell">
                    ${row.meetings.map(m => `<div>${m.day}</div>`).join("")}
                  </td>
                  <td class="schedule-cell">
                    ${row.meetings.map(m => `<div>${m.time}</div>`).join("")}
                  </td>
                  <td>
                    ${row.meetings.map(m => `<div>${m.room}</div>`).join("")}
                  </td>
                  <td style="text-align:right;">${row.units || ""}</td>
                </tr>
              `).join("")}
              <tr>
                <td colspan="6" style="text-align:right;font-weight:bold;">TOTAL UNITS</td>
                <td style="text-align:right;font-weight:bold;">${totalUnits}</td>
              </tr>
            </tbody>
          </table>
          <div style="margin-top:32px; display:flex; gap:48px;">
            <div style="flex:1;">
              <div style="font-weight:700;font-size:20px;margin-bottom:18px;">MISCELLANEOUS FEES</div>
              <table style="width:100%;border-collapse:collapse;margin-bottom:18px;">
                <thead>
                  <tr>
                    <th style="text-align:left;font-weight:500;"> </th>
                    <th style="text-align:right;font-weight:500;">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${fees.map(fee => `
                    <tr>
                      <td style="padding:6px 0;">
                        ${fee.label} ${fee.formula ? `<span style='color:#888;font-size:13px;'>${fee.formula}</span>` : ''}
                      </td>
                      <td style="text-align:right;padding:6px 0;">
                        ₱ ${fee.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  `).join("")}
                  <tr>
                    <td style="font-weight:700;padding-top:10px;">Total:</td>
                    <td style="text-align:right;font-weight:700;font-size:17px;padding-top:10px;">
                      ₱ ${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div style="font-weight:700;color:#18803b;font-size:18px;margin:30px 0 8px 0;">
                OPTION 1: CASH PAYMENT WITH DISCOUNT
              </div>
              <div style="font-size:18px;margin-bottom:4px;font-weight:bold;color:#222;">
                ₱ ${totalWithDiscount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
              <div style="font-size:16px;margin-bottom:18px;">
                If full payment is made on or before ${formatDate(cashWithDiscountDate)}.
              </div>
              <div style="font-weight:700;color:#18803b;font-size:18px;margin:30px 0 8px 0;">
                OPTION 2: CASH PAYMENT WITHOUT DISCOUNT
              </div>
              <div style="font-size:18px;margin-bottom:4px;font-weight:bold;color:#222;">
                ₱ ${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
              <div style="font-size:16px;margin-bottom:18px;">
                If full payment is made after ${formatDate(cashWithoutDiscountDate)}.
              </div>
            </div>
            <div style="flex:1;">
              <div style="font-weight:700;color:#18803b;font-size:20px;margin-bottom:18px;">
                OPTION 3: INSTALLMENT PAYMENT
              </div>
              <table style="width:100%;border-collapse:collapse;margin-bottom:18px;">
                <tbody>
                  ${installmentDates.map((date, idx) => `
                    <tr>
                      <td style="padding:6px 0;">
                        ${["Downpayment", "1st Installment", "2nd Installment", "3rd Installment", "4th Installment"][idx]}
                      </td>
                      <td style="padding:6px 0;">
                        ${formatDate(date)}
                      </td>
                      <td style="padding:6px 0;">
                        ₱ ${installment.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  `).join("")}
                  <tr>
                    <td style="font-weight:700;padding-top:10px;">Total:</td>
                    <td></td>
                    <td style="text-align:right;font-weight:700;font-size:17px;padding-top:10px;">
                      ₱ ${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div style="margin-top:32px; font-size:13px; background:#fff8f1; border:1px solid #ff9800; border-radius:6px; padding:12px; color:#e67e22;">
            <div style="font-weight:bold;">IMPORTANT NOTICE:</div>
            <div style="color:#444;">
              1. Students with requirement deficiency will be placed as a PROBATIONARY STUDENT.<br>
              2. This registration form serves as the student's proof of enrollment. Students must attend only classes/sections as indicated on the registration form.<br>
              3. This computer-generated REGISTRATION FORM has been electronically approved by the Student, Program Head/SHS Principal, College Treasurer, and College Registrar. No signature required.<br>
            </div>
          </div>
          <div style="font-size:13px; margin-top:24px;">
            Printed on: ${formatDate(new Date().toISOString())}<br>
            Officially Enrolled on: ${formatDate(student.enrolledDate || new Date().toISOString())}
          </div>
        </div>
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  }

  return (
    <>
      {/* COR Document */}
      <div style={{ maxWidth: 1200, margin: "24px auto", background: "#fff", borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.15)", overflow: "hidden" }}>
        {/* Header with Download Button */}
        <div style={{ background: "#0f5f3b", color: "#fff", padding: "24px 48px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 22, marginBottom: 8 }}>
              Certificate of Registration
            </div>
            <div style={{ fontSize: 15, opacity: 0.9 }}>
              {schoolYear} {semester}
            </div>
          </div>
          <button
            onClick={handlePrint}
            style={{
              background: "#fff",
              color: "#18803b",
              fontWeight: 700,
              fontSize: 16,
              borderRadius: 6,
              padding: "12px 32px",
              border: "none",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              letterSpacing: 0.5
            }}
          >
            DOWNLOAD / PRINT
          </button>
        </div>

        {/* COR Document Content */}
        <div
          ref={corRef}
          style={{
            width: "100%",
            maxWidth: 1100,
            margin: "0 auto",
            padding: "48px 60px",
            background: "#fff",
            fontSize: 14,
            lineHeight: 1.5,
            boxSizing: "border-box",
          }}
        >
          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: 32, borderBottom: "2px solid #18803b", paddingBottom: 16 }}>
            <div style={{ fontSize: 26, fontWeight: "bold", color: "#18803b" }}>Manila Adventist College</div>
            <div style={{ fontSize: 17, marginTop: 4 }}>CERTIFICATE OF REGISTRATION</div>
            <div style={{ fontSize: 20, marginTop: 8, fontWeight: 600 }}>
              {schoolYear} {semester}
            </div>
          </div>

          {/* Student Info */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, fontSize: 16 }}>
              <div><span style={{ fontWeight: 500 }}>Name:</span> <b>{studentName}</b></div>
              <div><span style={{ fontWeight: 500 }}>Student ID:</span> <b>{studentIdNum}</b></div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16 }}>
              <div><span style={{ fontWeight: 500 }}>Program:</span> <b>{program}</b></div>
              <div><span style={{ fontWeight: 500 }}>Year:</span> <b>{yearLevel}</b></div>
            </div>
          </div>

          {/* Subjects Table */}
          <div style={{ marginBottom: 40 }}>
            {groupedCourses.length > 0 ? (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f7f7f7" }}>
                    {["Subject Code", "Subject Description", "Section", "Day", "Schedule", "Room", "Units"].map((h) => (
                      <th key={h} style={{ padding: 12, border: "1px solid #ddd", fontSize: 14, fontWeight: 600, textAlign: "left" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groupedCourses.map((row, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: 12, border: "1px solid #ddd", fontSize: 14 }}>{row.courseId || ""}</td>
                      <td style={{ padding: 12, border: "1px solid #ddd", fontSize: 14 }}>{row.courseName || ""}</td>
                      <td style={{ padding: 12, border: "1px solid #ddd", fontSize: 14, textAlign: "center" }}>{row.section || ""}</td>
                      <td style={{ padding: 12, border: "1px solid #ddd", fontSize: 14 }}>
                        {row.meetings.map((m, i) => <div key={i} style={{ whiteSpace: "pre-line" }}>{m.day}</div>)}
                      </td>
                      <td style={{ padding: 12, border: "1px solid #ddd", fontSize: 14 }}>
                        {row.meetings.map((m, i) => <div key={i} style={{ whiteSpace: "pre-line" }}>{m.time}</div>)}
                      </td>
                      <td style={{ padding: 12, border: "1px solid #ddd", fontSize: 14, textAlign: "center" }}>
                        {row.meetings.map((m, i) => <div key={i}>{m.room}</div>)}
                      </td>
                      <td style={{ padding: 12, border: "1px solid #ddd", fontSize: 14, textAlign: "center" }}>{row.units || ""}</td>
                    </tr>
                  ))}
                  <tr style={{ background: "#f7f7f7" }}>
                    <td colSpan={6} style={{ padding: 12, border: "1px solid #ddd", textAlign: "right", fontWeight: 700, fontSize: 15 }}>TOTAL UNITS</td>
                    <td style={{ padding: 12, border: "1px solid #ddd", textAlign: "center", fontWeight: 700, fontSize: 15 }}>{totalUnits}</td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <div style={{ padding: 24, textAlign: "center", color: "#888" }}>
                No courses available for {yearLevel} - {semester}
              </div>
            )}
          </div>

          {/* Fees Section */}
          <div style={{ display: "flex", gap: 60, marginBottom: 40 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 19, marginBottom: 16, color: "#18803b", borderBottom: "2px solid #18803b", paddingBottom: 8 }}>
                MISCELLANEOUS FEES
              </div>
              <table style={{ width: "100%", marginBottom: 16 }}>
                <tbody>
                  {fees.map((fee, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: "8px 0", fontSize: 14 }}>
                        {fee.label}{" "}
                        {fee.formula && <span style={{ color: "#888", fontSize: 13 }}>{fee.formula}</span>}
                      </td>
                      <td style={{ textAlign: "right", padding: "8px 0", fontSize: 14, fontWeight: 500 }}>
                        ₱ {fee.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: "2px solid #333" }}>
                    <td style={{ fontWeight: 700, paddingTop: 10, fontSize: 15 }}>Total:</td>
                    <td style={{ textAlign: "right", fontWeight: 700, fontSize: 17, paddingTop: 10 }}>
                      ₱ {total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div style={{ fontWeight: 700, color: "#18803b", fontSize: 16, marginTop: 24, marginBottom: 8 }}>
                OPTION 1: CASH PAYMENT WITH DISCOUNT
              </div>
              <div style={{ fontSize: 17, fontWeight: "bold", color: "#222", marginBottom: 4 }}>
                ₱ {totalWithDiscount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
              <div style={{ fontSize: 14, marginBottom: 16, color: "#555" }}>
                If full payment is made on or before {formatDate(cashWithDiscountDate)}.
              </div>

              <div style={{ fontWeight: 700, color: "#18803b", fontSize: 16, marginTop: 16, marginBottom: 8 }}>
                OPTION 2: CASH PAYMENT WITHOUT DISCOUNT
              </div>
              <div style={{ fontSize: 17, fontWeight: "bold", color: "#222", marginBottom: 4 }}>
                ₱ {total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
              <div style={{ fontSize: 14, marginBottom: 16, color: "#555" }}>
                If full payment is made after {formatDate(cashWithoutDiscountDate)}.
              </div>
            </div>

            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: "#18803b", fontSize: 19, marginBottom: 16, borderBottom: "2px solid #18803b", paddingBottom: 8 }}>
                OPTION 3: INSTALLMENT PAYMENT
              </div>
              <table style={{ width: "100%" }}>
                <tbody>
                  {installmentDates.map((date, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: "8px 0", fontSize: 14 }}>
                        {["Downpayment", "1st Installment", "2nd Installment", "3rd Installment", "4th Installment"][idx]}
                      </td>
                      <td style={{ padding: "8px 0", fontSize: 14 }}>{formatDate(date)}</td>
                      <td style={{ textAlign: "right", padding: "8px 0", fontSize: 14, fontWeight: 500 }}>
                        ₱ {installment.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: "2px solid #333" }}>
                    <td style={{ fontWeight: 700, paddingTop: 10, fontSize: 15 }}>Total:</td>
                    <td></td>
                    <td style={{ textAlign: "right", fontWeight: 700, fontSize: 17, paddingTop: 10 }}>
                      ₱ {total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Notice */}
          <div style={{ marginTop: 32, fontSize: 13, background: "#fff8f1", border: "1px solid #ff9800", borderRadius: 6, padding: 18, color: "#e67e22" }}>
            <div style={{ fontWeight: "bold", marginBottom: 8 }}>IMPORTANT NOTICE:</div>
            <div style={{ color: "#555", lineHeight: 1.6 }}>
              1. Students with requirement deficiency will be placed as a PROBATIONARY STUDENT.<br />
              2. This registration form serves as the student's proof of enrollment. Students must attend only classes/sections as indicated on the registration form.<br />
              3. This computer-generated REGISTRATION FORM has been electronically approved by the Student, Program Head/SHS Principal, College Treasurer, and College Registrar. No signature required.
            </div>
          </div>

          {/* Footer */}
          <div style={{ fontSize: 13, marginTop: 24, paddingTop: 16, borderTop: "1px solid #ddd", color: "#666" }}>
            <div>Printed on: {formatDate(new Date().toISOString())}</div>
            <div>Officially Enrolled on: {formatDate(student.enrolledDate || student.cor?.corPdfGeneratedAt || new Date().toISOString())}</div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function EnrolledCORPage({ student: propStudent }) {
  const todayKey = useCalendarClock();
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const [localStudent, setLocalStudent] = useState(propStudent || location.state?.student || null);
  const [selectedTerm, setSelectedTerm] = useState("");
  const [selectedSchoolYear, setSelectedSchoolYear] = useState("");
  const [currentSchoolYear, setCurrentSchoolYear] = useState("");
  const [currentSemester, setCurrentSemester] = useState("");
  const [announcementData, setAnnouncementData] = useState(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  // Layout constants
  const SIDEBAR_WIDTH = 240;
  const HEADER_HEIGHT = 70;
  const PAGE_PADDING = 32;
  const CONTENT_MAX = 1100;
  const [scrollOffset, setScrollOffset] = useState(0);
  const [lastScrollTop, setLastScrollTop] = useState(0);


  // Track announcement settings and refresh the active term when the calendar day changes.
  useEffect(() => {
    const announcementRef = doc(getFirestore(app), "systemSettings", "announcement");
    return onSnapshot(announcementRef, snapshot => {
      setAnnouncementData(snapshot.exists() ? snapshot.data() : null);
    }, error => console.error("Failed to listen to announcement settings:", error));
  }, []);

  useEffect(() => {
    const activeSemester = getActiveSemester(announcementData || {}, todayKey);
    setCurrentSemester(activeSemester === "first" ? "First Semester" : activeSemester === "second" ? "Second Semester" : "");
    setCurrentSchoolYear(activeSemester ? getAnnouncementSchoolYear(announcementData, activeSemester) : "");
  }, [announcementData, todayKey]);

  const handleProfile = (action) => {
    setProfileMenuOpen(false);
    if (action === "logout") {
      clearEnrolledSession();
      navigate("/enrolled/signin");
      return;
    }
  };

  // Get unique school years and terms from student termSchedules
  // termSchedules is an object with keys like "2024-2025_First Semester"
  const allSchedules = [];
  if (localStudent?.termSchedules && typeof localStudent.termSchedules === 'object') {
    Object.entries(localStudent.termSchedules).forEach(([termKey, schedArray]) => {
      if (Array.isArray(schedArray)) {
        allSchedules.push(...schedArray);
      }
    });
  }

  const schoolYears = allSchedules.length > 0
    ? [...new Set(allSchedules.map(s => s.schoolYear).filter(Boolean))]
    : [];

  const terms = allSchedules.length > 0
    ? [...new Set(allSchedules.map(s => s.semester).filter(Boolean))]
    : [];

  // Auto-select current term or first option on mount
  useEffect(() => {
    if (currentSchoolYear && schoolYears.includes(currentSchoolYear) && !selectedSchoolYear) {
      setSelectedSchoolYear(currentSchoolYear);
    } else if (schoolYears.length > 0 && !selectedSchoolYear) {
      setSelectedSchoolYear(schoolYears[0]);
    }
    
    if (currentSemester && terms.includes(currentSemester) && !selectedTerm) {
      setSelectedTerm(currentSemester);
    } else if (terms.length > 0 && !selectedTerm) {
      setSelectedTerm(terms[0]);
    }
  }, [terms, schoolYears, selectedTerm, selectedSchoolYear, currentSchoolYear, currentSemester]);

  async function resolveStudentDocByIdentifier(db, identifier) {
    if (!identifier) return null;
    const idStr = String(identifier).trim();

    try {
      const directRef = doc(db, "students", idStr);
      const directSnap = await getDoc(directRef);
      if (directSnap.exists()) return { id: directSnap.id, ...directSnap.data() };
    } catch (err) {
      // continue
    }

    let q = query(collection(db, "students"), where("idNumber", "==", idStr), limit(1));
    let snap = await getDocs(q);
    if (!snap.empty) return { id: snap.docs[0].id, ...snap.docs[0].data() };

    q = query(collection(db, "students"), where("uid", "==", idStr), limit(1));
    snap = await getDocs(q);
    if (!snap.empty) return { id: snap.docs[0].id, ...snap.docs[0].data() };

    if (idStr.includes("@")) {
      q = query(collection(db, "students"), where("email", "==", idStr), limit(1));
      snap = await getDocs(q);
      if (!snap.empty) return { id: snap.docs[0].id, ...snap.docs[0].data() };
    }

    return null;
  }

  useEffect(() => {
    if (localStudent) return;

    let cancelled = false;
    (async () => {
      try {
        const db = getFirestore(app);
        const pathSeg = (location.pathname || "").split("/").filter(Boolean).pop();

        const candidates = [
          params.id,
          params.slug,
          pathSeg,
          location.state?.student?.id,
          location.state?.student?.idNumber,
          propStudent?.id,
          propStudent?.idNumber
        ].filter(Boolean);

        for (const idCandidate of candidates) {
          if (cancelled) return;
          const found = await resolveStudentDocByIdentifier(db, idCandidate);
          if (found) {
            setLocalStudent(found);
            return;
          }
        }
      } catch (err) {
        console.error("EnrolledCOR: failed to resolve student:", err);
      }
    })();

    return () => { cancelled = true; };
  }, [params, location, propStudent, localStudent]);

  const [miscFees, setMiscFees] = useState([]);
  const [cashDiscount, setCashDiscount] = useState(0);
  const [cashWithDiscountDate, setCashWithDiscountDate] = useState("");
  const [cashWithoutDiscountDate, setCashWithoutDiscountDate] = useState("");
  const [installmentDates, setInstallmentDates] = useState([]);

  useEffect(() => {
    const fetchFees = async () => {
      const db = getFirestore(app);
      const ref = doc(db, "systemSettings", "miscellaneousFees");
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        setMiscFees(data.fees || []);
        setCashDiscount(data.cashDiscount || 0);
        setCashWithDiscountDate(data.cashWithDiscountDate || "");
        setCashWithoutDiscountDate(data.cashWithoutDiscountDate || "");
        setInstallmentDates(data.installmentDates || []);
      }
    };
    fetchFees();
  }, []);

  const fullName = localStudent ? `${localStudent.firstName || ""} ${localStudent.lastName || ""}`.trim() : "";
  const profilePicUrl = localStudent?.profilePicUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName || "Enrolled")}`;
  const navItems = [
    { key: "home", label: "Home", path: "/enrolled/dashboard" },
    { key: "pre", label: "Pre-Registration", path: "/enrolled/pre-registration" },
    { key: "curriculum", label: "Curriculum Overview", path: "/enrolled/curriculum-overview" },
    { key: "schedule", label: "Class Schedule", path: "/enrolled/class-schedule" },
    { key: "final", label: "COR", path: "/enrolled/cor" },
  ];

  // Handle scroll for hide-on-scroll behavior
  useEffect(() => {
    const mainElement = document.querySelector('[data-scroll-container]');
    if (!mainElement) return;

    const handleScroll = () => {
      const scrollTop = mainElement.scrollTop;
      if (scrollTop > lastScrollTop) {
        setScrollOffset(Math.min(scrollTop - lastScrollTop, HEADER_HEIGHT + SIDEBAR_WIDTH));
      } else {
        setScrollOffset(Math.max(scrollTop - lastScrollTop, -(HEADER_HEIGHT + SIDEBAR_WIDTH)));
      }
      setLastScrollTop(scrollTop);
    };

    mainElement.addEventListener('scroll', handleScroll);
    return () => mainElement.removeEventListener('scroll', handleScroll);
  }, [lastScrollTop]);

  return (
    <div style={{ display: "flex", minHeight: "100vh", fontFamily: "Inter, Arial, sans-serif", background: "#efefef" }}>
      {/* Sidebar */}
      <aside style={{ width: SIDEBAR_WIDTH, background: "#0f5f3b", color: "#fff", display: "flex", flexDirection: "column", position: "fixed", height: "100vh", left: 0, top: 0, transform: `translateY(${Math.max(-scrollOffset, -HEADER_HEIGHT)}px)`, transition: "transform 0.3s ease", zIndex: 1000 }}>
        <div style={{ padding: "28px 24px", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
          <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: 1 }}>EnrollEase</div>
        </div>
        <nav style={{ paddingTop: 24, flex: 1 }}>
          {navItems.map((n) => {
            const isActive = window.location.pathname.startsWith(n.path);
            return (
              <div
                key={n.key}
                onClick={() => navigate(n.path, { state: { student: localStudent } })}
                style={{
                  padding: "14px 24px",
                  margin: "4px 16px",
                  borderRadius: 8,
                  cursor: "pointer",
                  background: isActive ? "#ff6f2e" : "transparent",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 15,
                  transition: "all 0.2s",
                  boxShadow: isActive ? "0 2px 8px rgba(255,111,46,0.3)" : "none"
                }}
              >
                {n.label}
              </div>
            );
          })}
        </nav>
      </aside>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", marginLeft: SIDEBAR_WIDTH }}>
        {/* Header */}
        <header style={{ height: HEADER_HEIGHT, background: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", padding: `0 ${PAGE_PADDING}px`, boxSizing: "border-box", borderBottom: "1px solid #e0e0e0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", position: "sticky", top: 0, transform: `translateY(${-Math.min(scrollOffset, HEADER_HEIGHT)}px)`, transition: "transform 0.3s ease", zIndex: 999, position: "relative" }}>
          <div style={{ color: "#0f5f3b", fontWeight: 700, fontSize: 20 }}>Certificate of Registration</div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ color: "#0f5f3b", fontWeight: 600, fontSize: 15 }}>{fullName}</div>
            </div>
            <img
              src={profilePicUrl}
              alt="Profile"
              style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", cursor: "pointer", border: "3px solid #ff6f2e" }}
              onClick={() => setProfileMenuOpen(v => !v)}
            />
            {profileMenuOpen && (
              <div style={{ position: "absolute", top: HEADER_HEIGHT - 4, right: PAGE_PADDING, background: "#fff", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.13)", minWidth: 180, padding: "10px 0", zIndex: 100 }}>
                <div style={{ padding: "10px 24px", cursor: "pointer", color: "#ff6f2e", fontWeight: 600 }} onClick={() => handleProfile("logout")}>Log out</div>
              </div>
            )}
          </div>
        </header>

        {/* Main Content */}
        <main style={{ flex: 1, padding: PAGE_PADDING, boxSizing: "border-box", overflowY: "auto", marginTop: 0 }} data-scroll-container>
          <div style={{ maxWidth: CONTENT_MAX, margin: "0 auto" }}>
            {!localStudent ? (
              <div style={{ padding: 18, background: "#fff", borderRadius: 8, border: "1px solid #eee" }}>
                <div style={{ color: "#b71c1c", fontWeight: 700 }}>Student information not provided.</div>
                <div style={{ marginTop: 8, color: "#666" }}>This page needs student data to load the COR. Open it from the enrolled dashboard.</div>
              </div>
            ) : (
              <>
                {/* Filter Section */}
                <div style={{ background: "#fff", borderRadius: 8, padding: "24px", marginBottom: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
                  <div style={{ display: "flex", gap: "24px", alignItems: "flex-end" }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: "block", fontWeight: 600, marginBottom: "8px", color: "#222" }}>
                        School Year: <span style={{ color: "red" }}>*</span>
                      </label>
                      <select
                        value={selectedSchoolYear}
                        onChange={(e) => setSelectedSchoolYear(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          borderRadius: "6px",
                          border: "1px solid #ccc",
                          fontSize: "14px",
                          fontFamily: "inherit"
                        }}
                      >
                        {schoolYears.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={{ flex: 1 }}>
                      <label style={{ display: "block", fontWeight: 600, marginBottom: "8px", color: "#222" }}>
                        Semester: <span style={{ color: "red" }}>*</span>
                      </label>
                      <select
                        value={selectedTerm}
                        onChange={(e) => setSelectedTerm(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          borderRadius: "6px",
                          border: "1px solid #ccc",
                          fontSize: "14px",
                          fontFamily: "inherit"
                        }}
                      >
                        {terms.map((term) => (
                          <option key={term} value={term}>
                            {term}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* COR Content */}
                <EnrolledCORContent
                  student={localStudent}
                  miscFees={miscFees}
                  cashDiscount={cashDiscount}
                  cashWithDiscountDate={cashWithDiscountDate}
                  cashWithoutDiscountDate={cashWithoutDiscountDate}
                  installmentDates={installmentDates}
                  selectedSchoolYear={selectedSchoolYear}
                  selectedTerm={selectedTerm}
                />
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}